import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { data, rotulo } from '../formato';
import {
  Aviso, Campo, Carregando, Dialogo, ErroDaApi, Rn, Selo, Vazio,
} from '../ui';
import type { Tom } from '../ui';
import { CamposDaFicha, TabelaComFicha } from '../TabelaComFicha';
import type { ColunaComFicha } from '../TabelaComFicha';

/**
 * Usuários e acessos — por tipo, por espaço e por nível (onda 224, `RN-206`;
 * o espaço no lugar da área, onda 295, `RN-242`).
 *
 * «Temos que simplificar a tela de usuários e acessos. Pense numa forma de
 *  organizar por tipos de usuários, talvez por grupo de módulos […] hoje
 *  não consigo selecionar parcialmente cada módulo. Poderia criar as
 *  opções apenas visualização, edição ou nenhum.» (Humberto, 29/09/2026)
 *
 * A grade de trinta cadeados virou três camadas:
 *
 *  1. **Tipo de usuário** (o perfil). É o ponto de partida, e a maioria
 *     das pessoas fica só com ele. A aba «Tipos de usuário» mostra e
 *     ajusta cada tipo.
 *  2. **Acesso por espaço** — Hub, LabManager, Campo, Comum, Sistema —,
 *     com **Nenhum / Ver / Editar**. O botão do espaço muda tudo o que está
 *     nele; abrindo o espaço, ajusta-se unidade por unidade. A unidade é o
 *     que o servidor confere junto (a tabela `acesso`, 0205): quando duas
 *     telas partilham a mesma permissão, a tela diz, em vez de fingir dois
 *     cadeados que o servidor não separa.
 *  3. **Assinaturas e responsabilidades**, marcadas uma a uma. **Editar
 *     nunca inclui assinar** (`RN-206`): verificar, aprovar, emitir,
 *     conferir, cancelar OS e excluir rascunho são responsabilidade
 *     técnica, e segregação de funções.
 *
 * O que difere do tipo aparece com borda tracejada e um «voltar ao tipo».
 * Tudo o que se muda numa ficha grava junto, com um motivo só. Usuário
 * continua sem botão de apagar: desligar mata o acesso e mantém a autoria
 * (`RN-163`).
 *
 * O primeiro nível é o ESPAÇO (spec 03 §8): as cinco listas — e a ordem
 * delas — vêm da API (`espacos`, da tabela `espaco`), e cada unidade diz a
 * que espaço pertence. A «área» do menu antigo (Trabalho, Bancada, Saída,
 * SGQ) saiu da tela; a coluna continua no banco, sem uso aqui. «Comum» é o
 * espaço dos dados que mais de um espaço lê: não abre espaço, mas é um
 * grupo como os outros para quem administra o acesso.
 */

type Nivel = 0 | 1 | 2;
type NivelOuParcial = Nivel | null;

interface Unidade {
  codigo: string; nome: string; espaco: string; ordem: number; telas: string;
  ver: number; editar: number;
}
interface Espaco { codigo: string; nome: string; ordem: number; de_trabalho: boolean }
interface PermissaoDoCatalogo {
  codigo: string; descricao: string; acesso: string;
  tipo: 'ver' | 'editar' | 'assinatura';
}
interface TipoDeUsuario {
  codigo: string; nome: string; pessoas: number;
  niveis: Record<string, NivelOuParcial>;
  assinaturas: string[];
}
interface AcessoDaPessoa {
  acesso: string; nivel: NivelOuParcial; nivel_do_tipo: NivelOuParcial; excecao: boolean;
}
interface AssinaturaDaPessoa {
  codigo: string; vale: boolean; do_perfil: boolean; excecao: boolean;
}

type Situacao = 'ativo' | 'bloqueado' | 'desligado' | 'travado'
  | 'aguardando acesso';

export interface Usuario {
  id: string;
  email: string | null;
  login: string;
  nome: string | null;
  cargo: string | null;
  situacao: Situacao;
  perfis: string[];
  responsavel_tecnico: boolean;
  acoes: number;
  bloqueio_motivo: string | null;
  desligamento_motivo: string | null;
  convite_pendente: boolean;
  convite_expira_em: string | null;
  senha_por_definir: boolean;
  acessos: AcessoDaPessoa[];
  assinaturas: AssinaturaDaPessoa[];
}

export interface Perfil { id: string; codigo: string; nome: string }
export interface Candidato { id: string; nome: string; cargo: string | null; matricula: string | null }

interface Dados {
  usuarios: Usuario[]; perfis: Perfil[]; candidatos: Candidato[];
  espacos: Espaco[]; unidades: Unidade[]; permissoes: PermissaoDoCatalogo[];
  tipos: TipoDeUsuario[];
}

const NOME_NIVEL = ['Nenhum', 'Ver', 'Editar'] as const;
const LETRA = ['—', 'V', 'E'] as const;

const TOM: Record<Situacao, Tom> = {
  ativo: 'ok',
  bloqueado: 'critico',
  travado: 'atencao',
  desligado: 'neutro',
  'aguardando acesso': 'andamento',
};

/** `null` é «parcial» e tem de chegar à tela como tal; só a AUSÊNCIA vira 0. */
const nz = (v: NivelOuParcial | undefined): NivelOuParcial => (v === undefined ? 0 : v);

/** Unidade que tem nível: as que só têm assinaturas ficam de fora dos botões. */
const temNivel = (u: Unidade) => u.ver + u.editar > 0;
const maximo = (u: Unidade): Nivel => (u.editar > 0 ? 2 : 1);

/* ─── O rascunho: o que mudou e ainda não foi gravado ─────────────── */

interface Rascunho {
  perfis?: string[];
  niveis: Record<string, Nivel>;
  assinaturas: Record<string, boolean>;
  motivo: string;
}
const vazio = (): Rascunho => ({ niveis: {}, assinaturas: {}, motivo: '' });

/**
 * O que a ficha mostra, e o que ela compara. É o mesmo desenho para uma
 * pessoa e para um tipo: `base` é o que viria do tipo; `gravado` é o que
 * vale hoje.
 */
interface Alvo {
  chave: string;
  eTipo: boolean;
  somenteLeitura: boolean;
  gravadoNivel: (acesso: string) => NivelOuParcial;
  baseNivel: (acesso: string) => NivelOuParcial;
  gravadoAssina: (codigo: string) => boolean;
  baseAssina: (codigo: string) => boolean;
}

function alvoDaPessoa(u: Usuario, tipos: TipoDeUsuario[], r: Rascunho): Alvo {
  const acesso = new Map(u.acessos.map((a) => [a.acesso, a]));
  const assina = new Map(u.assinaturas.map((a) => [a.codigo, a]));
  const novos = r.perfis ? tipos.filter((t) => r.perfis!.includes(t.codigo)) : null;
  return {
    chave: u.id,
    eTipo: false,
    somenteLeitura: u.situacao === 'desligado',
    gravadoNivel: (a) => nz(acesso.get(a)?.nivel),
    baseNivel: (a) => (novos
      ? novos.reduce<NivelOuParcial>((m, t) => {
        const n = nz(t.niveis[a]);
        return m === null || n === null ? null : (Math.max(m, n) as Nivel);
      }, 0)
      : nz(acesso.get(a)?.nivel_do_tipo)),
    gravadoAssina: (c) => assina.get(c)?.vale ?? false,
    baseAssina: (c) => (novos
      ? novos.some((t) => t.assinaturas.includes(c))
      : assina.get(c)?.do_perfil ?? false),
  };
}

function alvoDoTipo(t: TipoDeUsuario): Alvo {
  return {
    chave: t.codigo,
    eTipo: true,
    somenteLeitura: t.codigo === 'admin',
    gravadoNivel: (a) => nz(t.niveis[a]),
    baseNivel: (a) => nz(t.niveis[a]),
    gravadoAssina: (c) => t.assinaturas.includes(c),
    baseAssina: (c) => t.assinaturas.includes(c),
  };
}

/** O nível que a ficha mostra: o do rascunho, ou (trocado o tipo) o do tipo novo. */
function nivelVisto(alvo: Alvo, r: Rascunho, a: string): NivelOuParcial {
  if (a in r.niveis) return r.niveis[a];
  return r.perfis ? alvo.baseNivel(a) : alvo.gravadoNivel(a);
}

function assinaVisto(alvo: Alvo, r: Rascunho, c: string, unidade: Unidade | undefined,
  verNivel: (a: string) => NivelOuParcial): boolean {
  if (unidade && temNivel(unidade) && verNivel(unidade.codigo) === 0) return false;
  if (c in r.assinaturas) return r.assinaturas[c];
  return r.perfis ? alvo.baseAssina(c) : alvo.gravadoAssina(c);
}

/* ─── A caixinha do nível: forma + letra + cor ────────────────────── */

interface Resumo { max: Nivel; misto: boolean; excecao: boolean; texto: string }

function resumir(valores: NivelOuParcial[], excecao: boolean): Resumo {
  const numeros = valores.filter((v): v is Nivel => v !== null);
  const max = (numeros.length ? Math.max(...numeros) : 1) as Nivel;
  const misto = valores.includes(null) || new Set(valores).size > 1;
  const conta = (n: Nivel) => valores.filter((v) => v === n).length;
  const parciais = valores.filter((v) => v === null).length;
  const texto = misto
    ? `Misto: ${conta(2)} editar, ${conta(1)} ver, ${conta(0)} nenhum`
      + (parciais ? `, ${parciais} parcial` : '')
    : NOME_NIVEL[max];
  return { max, misto, excecao, texto };
}

function Caixa({ r }: { r: Resumo }) {
  const t = r.texto + (r.excecao ? ' · tem exceção individual' : '');
  return (
    <span className={`nv n${r.max}${r.misto ? ' misto' : ''}${r.excecao ? ' exc' : ''}`}
      title={t} aria-label={t} role="img">
      {LETRA[r.max]}
    </span>
  );
}

function Segmentos({ rotulo: nome, valor, maximoPermitido, desligado, aoEscolher, pequeno }: {
  rotulo: string; valor: NivelOuParcial | 'misto'; maximoPermitido: Nivel;
  desligado?: boolean; aoEscolher: (n: Nivel) => void; pequeno?: boolean;
}) {
  return (
    <div className={`seg-nivel${pequeno ? ' pequeno' : ''}`} role="group" aria-label={nome}>
      {([0, 1, 2] as Nivel[]).map((n) => {
        const alem = n > maximoPermitido;
        return (
          <button key={n} type="button" className={`b${n}`}
            aria-pressed={valor === n}
            disabled={desligado || alem}
            title={alem ? 'Esta unidade não tem o que editar: o máximo é Ver.' : undefined}
            onClick={() => aoEscolher(n)}>
            {NOME_NIVEL[n]}
          </button>
        );
      })}
    </div>
  );
}

/* ─── A tela ──────────────────────────────────────────────────────── */

export function Usuarios() {
  const [dados, setDados] = useState<Dados | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [busca, setBusca] = useState('');
  const [aba, setAba] = useState<'pessoas' | 'tipos'>('pessoas');
  const [novo, setNovo] = useState(false);
  const [rascunhos, setRascunhos] = useState<Record<string, Rascunho>>({});
  const [alvo, setAlvo] = useState<
    { tipo: 'bloquear' | 'liberar' | 'desligar' | 'convidar'; u: Usuario } | null>(null);

  const carregar = useCallback(async (q: string) => {
    setErro(null);
    try {
      const r = await api<Dados>(`/usuarios${q ? `?busca=${encodeURIComponent(q)}` : ''}`);
      setDados(r);
    } catch (e) { setErro(e); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { void carregar(busca); }, 250);
    return () => clearTimeout(t);
  }, [busca, carregar]);

  /* As unidades de cada espaço, na ordem em que a API as devolve (a de dentro
     do espaço). Espaço sem unidade fica com a lista vazia, e a ficha o omite. */
  const porEspaco = useMemo(() => {
    const m = new Map<string, Unidade[]>();
    for (const e of dados?.espacos ?? []) m.set(e.codigo, []);
    for (const u of dados?.unidades ?? []) m.get(u.espaco)?.push(u);
    return m;
  }, [dados]);

  const rascunhoDe = (k: string) => rascunhos[k] ?? vazio();
  const mudarRascunho = (k: string, f: (r: Rascunho) => Rascunho) =>
    setRascunhos((todos) => ({ ...todos, [k]: f(todos[k] ?? vazio()) }));
  const descartar = (k: string) => setRascunhos((todos) => {
    const { [k]: _fora, ...resto } = todos;
    return resto;
  });

  if (erro && !dados) return <ErroDaApi erro={erro} />;
  if (!dados) return <Carregando />;

  const resumoDaPessoa = (u: Usuario, espaco: string) => {
    const unidades = (porEspaco.get(espaco) ?? []).filter(temNivel);
    const m = new Map(u.acessos.map((a) => [a.acesso, a]));
    return resumir(unidades.map((x) => nz(m.get(x.codigo)?.nivel)),
      unidades.some((x) => m.get(x.codigo)?.excecao));
  };
  const resumoDoTipo = (t: TipoDeUsuario, espaco: string) => {
    const unidades = (porEspaco.get(espaco) ?? []).filter(temNivel);
    return resumir(unidades.map((x) => nz(t.niveis[x.codigo])), false);
  };
  const nomeDoTipo = (c: string) => dados.tipos.find((t) => t.codigo === c)?.nome ?? rotulo(c);

  const colunasPessoas: ColunaComFicha<Usuario>[] = [
    {
      chave: 'pessoa',
      cabeca: <th scope="col">Pessoa</th>,
      celula: (u) => (
        <th scope="row" className="acesso-pessoa">
          <strong>{u.nome ?? '(sem funcionário vinculado)'}</strong>
          <span className="sub mono">{u.login ?? u.email}</span>
        </th>
      ),
    },
    {
      chave: 'tipo',
      cabeca: <th scope="col">Tipo de usuário</th>,
      celula: (u) => {
        const exc = u.acessos.filter((a) => a.excecao).length
          + u.assinaturas.filter((a) => a.excecao).length;
        const pendente = rascunhos[u.id] && contarMudancas(u, dados, rascunhos[u.id]) > 0;
        return (
          <td className="acesso-tipo">
            {u.perfis.map(nomeDoTipo).join(' + ') || 'sem tipo'}
            {exc > 0 && <span className="sub excecoes">{exc} exceç{exc > 1 ? 'ões' : 'ão'}</span>}
            {pendente && <span className="sub pendente">alterações por gravar</span>}
          </td>
        );
      },
    },
    ...dados.espacos.map<ColunaComFicha<Usuario>>((e) => ({
      chave: `espaco-${e.codigo}`,
      cabeca: <th scope="col" className="col-espaco">{e.nome}</th>,
      celula: (u) => <td className="col-espaco"><Caixa r={resumoDaPessoa(u, e.codigo)} /></td>,
    })),
    {
      chave: 'trilha', detalhe: true,
      cabeca: <th scope="col" className="col-opcional num">Trilha</th>,
      celula: (u) => <td className="col-opcional num">{u.acoes}</td>,
    },
    {
      /* Detalhe: com a ficha aberta a situação vai para o subtítulo dela,
         e os cinco espaços — que são o assunto desta tela — ficam. */
      chave: 'situacao', detalhe: true,
      cabeca: <th scope="col">Situação</th>,
      celula: (u) => (
        <td>
          <Selo tom={TOM[u.situacao]}>{rotulo(u.situacao)}</Selo>
          {u.convite_pendente && u.convite_expira_em && (
            <span className="sub">convite até {data(u.convite_expira_em)}</span>
          )}
        </td>
      ),
    },
  ];

  const colunasTipos: ColunaComFicha<TipoDeUsuario>[] = [
    {
      chave: 'tipo',
      cabeca: <th scope="col">Tipo de usuário</th>,
      celula: (t) => (
        <th scope="row" className="acesso-pessoa">
          <strong>{t.nome}</strong>
          {rascunhos[`tipo:${t.codigo}`] && <span className="sub pendente">alterações por gravar</span>}
        </th>
      ),
    },
    ...dados.espacos.map<ColunaComFicha<TipoDeUsuario>>((e) => ({
      chave: `espaco-${e.codigo}`,
      cabeca: <th scope="col" className="col-espaco">{e.nome}</th>,
      celula: (t) => <td className="col-espaco"><Caixa r={resumoDoTipo(t, e.codigo)} /></td>,
    })),
    {
      chave: 'assinaturas', detalhe: true,
      cabeca: <th scope="col" className="col-opcional num">Assinaturas</th>,
      celula: (t) => <td className="col-opcional num">{t.assinaturas.length}</td>,
    },
    {
      chave: 'pessoas',
      cabeca: <th scope="col" className="num">Pessoas</th>,
      celula: (t) => <td className="num">{t.pessoas}</td>,
    },
  ];

  const aoGravar = () => void carregar(busca);

  return (
    <div className="tela-acessos">
      <div className="cabeca">
        <h1>Usuários e acessos</h1>
        <p className="sub">
          Cada pessoa tem um tipo de usuário. O tipo define o acesso por espaço;
          a ficha mostra e ajusta as exceções.
        </p>
      </div>

      <div className="abas" role="tablist" aria-label="Pessoas ou tipos">
        <button type="button" role="tab" aria-selected={aba === 'pessoas'}
          className={aba === 'pessoas' ? 'ativa' : undefined}
          onClick={() => setAba('pessoas')}>Pessoas</button>
        <button type="button" role="tab" aria-selected={aba === 'tipos'}
          className={aba === 'tipos' ? 'ativa' : undefined}
          onClick={() => setAba('tipos')}>Tipos de usuário</button>
      </div>

      <ul className="legenda-acesso" aria-label="Legenda">
        <li><Caixa r={{ max: 0, misto: false, excecao: false, texto: 'Nenhum' }} /> Nenhum: as telas somem do menu</li>
        <li><Caixa r={{ max: 1, misto: false, excecao: false, texto: 'Ver' }} /> Ver: consulta, abre e baixa (no Mapa, o PDF do acervo pede «Baixar documentos do acervo»)</li>
        <li><Caixa r={{ max: 2, misto: false, excecao: false, texto: 'Editar' }} /> Editar: cria, altera, lança</li>
        <li><Caixa r={{ max: 1, misto: true, excecao: false, texto: 'Misto' }} /> Misto: unidades do espaço com níveis diferentes</li>
        <li><Caixa r={{ max: 1, misto: false, excecao: true, texto: 'Exceção' }} /> Borda tracejada: exceção desta pessoa</li>
      </ul>

      {erro ? <ErroDaApi erro={erro} /> : null}

      {aba === 'pessoas' ? (
        <div className="cartao">
          <div className="cartao-cabeca">
            <h2>Pessoas</h2>
            <span className="sub">{dados.usuarios.length} usuário(s)</span>
            <input type="search" placeholder="Nome, login ou e-mail" value={busca}
              onChange={(e) => setBusca(e.target.value)} aria-label="Buscar usuário" />
            <button type="button" className="botao" onClick={() => setNovo(true)}>
              Novo usuário
            </button>
          </div>
          {dados.usuarios.length === 0 ? (
            <Vazio>Nenhum usuário encontrado.</Vazio>
          ) : (
            <TabelaComFicha
              linhas={dados.usuarios}
              chave={(u) => u.id}
              nome={(u) => u.nome ?? u.login}
              rotulo="Pessoas e acesso por espaço"
              classeDaLinha={(u) => (u.situacao === 'desligado' ? 'apagada' : undefined)}
              colunas={colunasPessoas}
              ficha={(u) => ({
                rotulo: 'Pessoa',
                titulo: u.nome ?? u.login,
                sub: (
                  <>
                    <span className="mono">{u.login}</span>
                    <span><Selo tom={TOM[u.situacao]}>{rotulo(u.situacao)}</Selo></span>
                  </>
                ),
                corpo: (
                  <EditorDeAcesso
                    key={u.id}
                    dados={dados}
                    porEspaco={porEspaco}
                    alvo={alvoDaPessoa(u, dados.tipos, rascunhoDe(u.id))}
                    rascunho={rascunhoDe(u.id)}
                    pessoa={u}
                    mudar={(f) => mudarRascunho(u.id, f)}
                    descartar={() => descartar(u.id)}
                    gravado={() => { descartar(u.id); aoGravar(); }}
                    acaoDaConta={(tipo) => setAlvo({ tipo, u })}
                  />
                ),
              })}
            />
          )}
          <p className="rodape-regra">
            Editar nunca inclui assinar <Rn codigo="RN-206" />: verificar,
            aprovar, emitir, conferir e cancelar são marcados um a um. Usuário
            não se apaga <Rn codigo="RN-163" />: desligar encerra o acesso e
            mantém a autoria de tudo que a pessoa assinou.
          </p>
        </div>
      ) : (
        <div className="cartao">
          <div className="cartao-cabeca">
            <h2>Tipos de usuário</h2>
            <span className="sub">
              O ponto de partida de cada pessoa. Mudar um tipo muda todos que o usam.
            </span>
          </div>
          <TabelaComFicha
            linhas={dados.tipos}
            chave={(t) => t.codigo}
            nome={(t) => t.nome}
            rotulo="Tipos de usuário e acesso por espaço"
            colunas={colunasTipos}
            ficha={(t) => ({
              rotulo: 'Tipo de usuário',
              titulo: t.nome,
              corpo: (
                <EditorDeAcesso
                  key={t.codigo}
                  dados={dados}
                  porEspaco={porEspaco}
                  alvo={alvoDoTipo(t)}
                  rascunho={rascunhoDe(`tipo:${t.codigo}`)}
                  tipo={t}
                  mudar={(f) => mudarRascunho(`tipo:${t.codigo}`, f)}
                  descartar={() => descartar(`tipo:${t.codigo}`)}
                  gravado={() => { descartar(`tipo:${t.codigo}`); aoGravar(); }}
                />
              ),
            })}
          />
        </div>
      )}

      {alvo?.tipo === 'convidar' && (
        <FormConvite usuario={alvo.u} aoFechar={() => setAlvo(null)}
          aoPronto={() => void carregar(busca)} />
      )}
      {alvo && alvo.tipo !== 'convidar' && (
        <FormAcao tipo={alvo.tipo} usuario={alvo.u} perfis={dados.perfis}
          aoFechar={() => setAlvo(null)}
          aoPronto={() => { setAlvo(null); void carregar(busca); }} />
      )}
      {novo && (
        <FormNovo perfis={dados.perfis} candidatos={dados.candidatos}
          aoFechar={() => setNovo(false)}
          aoPronto={() => { setNovo(false); void carregar(busca); }} />
      )}
    </div>
  );
}

/** Quantas coisas o rascunho de uma pessoa muda de fato. */
function contarMudancas(u: Usuario, dados: Dados, r: Rascunho): number {
  return listarMudancas(alvoDaPessoa(u, dados.tipos, r), r, dados, u.perfis).length;
}

function listarMudancas(alvo: Alvo, r: Rascunho, dados: Dados, perfisAtuais?: string[]): string[] {
  const out: string[] = [];
  const nomeTipo = (c: string) => dados.tipos.find((t) => t.codigo === c)?.nome ?? c;
  if (r.perfis && perfisAtuais
      && r.perfis.slice().sort().join() !== perfisAtuais.slice().sort().join()) {
    out.push(`Tipo: ${perfisAtuais.map(nomeTipo).join(' + ') || '—'} → `
      + `${r.perfis.map(nomeTipo).join(' + ')}`);
  }
  const ver = (a: string) => nivelVisto(alvo, r, a);
  for (const u of dados.unidades.filter(temNivel)) {
    const antes = alvo.gravadoNivel(u.codigo);
    const depois = ver(u.codigo);
    if (antes !== depois) {
      out.push(`${u.nome}: ${antes === null ? 'Parcial' : NOME_NIVEL[antes]} → `
        + `${depois === null ? 'Parcial' : NOME_NIVEL[depois]}`);
    }
  }
  const unidade = new Map(dados.unidades.map((x) => [x.codigo, x]));
  for (const p of dados.permissoes.filter((x) => x.tipo === 'assinatura')) {
    const antes = alvo.gravadoAssina(p.codigo);
    const depois = assinaVisto(alvo, r, p.codigo, unidade.get(p.acesso), ver);
    if (antes !== depois) out.push(`${p.descricao}: ${depois ? 'concedida' : 'retirada'}`);
  }
  return out;
}

/* ─── A ficha: o editor de uma pessoa ou de um tipo ──────────────── */

function EditorDeAcesso({
  dados, porEspaco, alvo, rascunho: r, pessoa, tipo, mudar, descartar, gravado, acaoDaConta,
}: {
  dados: Dados;
  porEspaco: Map<string, Unidade[]>;
  alvo: Alvo;
  rascunho: Rascunho;
  pessoa?: Usuario;
  tipo?: TipoDeUsuario;
  mudar: (f: (r: Rascunho) => Rascunho) => void;
  descartar: () => void;
  gravado: () => void;
  acaoDaConta?: (t: 'bloquear' | 'liberar' | 'desligar' | 'convidar') => void;
}) {
  const [abertas, setAbertas] = useState<Set<string>>(new Set());
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const ver = (a: string) => nivelVisto(alvo, r, a);
  const unidade = new Map(dados.unidades.map((x) => [x.codigo, x]));
  const mudancas = listarMudancas(alvo, r, dados, pessoa?.perfis);
  const ro = alvo.somenteLeitura;

  /*
   * Escolher um nível: se o escolhido é o que já está gravado, a unidade
   * sai do rascunho — não há o que gravar. Na pessoa, se for o do tipo,
   * a exceção some ao gravar (a API apaga a linha individual).
   */
  const escolher = (a: string, n: Nivel) => mudar((x) => {
    const niveis = { ...x.niveis };
    if (!x.perfis && alvo.gravadoNivel(a) === n) delete niveis[a];
    else niveis[a] = n;
    return { ...x, niveis };
  });
  const escolherEspaco = (espaco: string, n: Nivel) => mudar((x) => {
    const niveis = { ...x.niveis };
    for (const u of (porEspaco.get(espaco) ?? []).filter(temNivel)) {
      const v = Math.min(n, maximo(u)) as Nivel;
      if (!x.perfis && alvo.gravadoNivel(u.codigo) === v) delete niveis[u.codigo];
      else niveis[u.codigo] = v;
    }
    return { ...x, niveis };
  });
  const marcar = (c: string, v: boolean) => mudar((x) => {
    const assinaturas = { ...x.assinaturas };
    if (!x.perfis && alvo.gravadoAssina(c) === v) delete assinaturas[c];
    else assinaturas[c] = v;
    return { ...x, assinaturas };
  });

  const excecaoNivel = (a: string) => {
    if (alvo.eTipo) return false;
    const base = alvo.baseNivel(a);
    return base !== null && ver(a) !== base;
  };
  const excecaoAssina = (c: string) => !alvo.eTipo
    && assinaVisto(alvo, r, c, unidade.get(dados.permissoes.find((p) => p.codigo === c)?.acesso ?? ''), ver)
      !== alvo.baseAssina(c);

  const voltarTudo = () => mudar((x) => {
    const niveis: Record<string, Nivel> = {};
    for (const u of dados.unidades.filter(temNivel)) {
      const b = alvo.baseNivel(u.codigo);
      if (b !== null && b !== alvo.gravadoNivel(u.codigo)) niveis[u.codigo] = b;
    }
    const assinaturas: Record<string, boolean> = {};
    for (const p of dados.permissoes.filter((y) => y.tipo === 'assinatura')) {
      const b = alvo.baseAssina(p.codigo);
      if (b !== alvo.gravadoAssina(p.codigo)) assinaturas[p.codigo] = b;
    }
    return { ...x, niveis, assinaturas };
  });

  const gravar = async () => {
    setEnviando(true); setErro(null);
    try {
      /* O que a unidade em «Nenhum» levou junto também vai: a API retira
         as assinaturas dela, e a tela mostra isso antes de gravar. */
      const assinaturas = { ...r.assinaturas };
      const caminho = alvo.eTipo
        ? `/usuarios/tipos/${alvo.chave}/acessos`
        : `/usuarios/${alvo.chave}/acessos`;
      await api(caminho, {
        metodo: 'PUT',
        corpo: {
          ...(r.perfis && !alvo.eTipo ? { perfis: r.perfis } : {}),
          niveis: r.niveis,
          assinaturas,
          motivo: r.motivo.trim(),
        },
      });
      gravado();
    } catch (e) { setErro(e); } finally { setEnviando(false); }
  };

  const nExcecoes = pessoa
    ? dados.unidades.filter((u) => temNivel(u) && excecaoNivel(u.codigo)).length
      + dados.permissoes.filter((p) => p.tipo === 'assinatura' && excecaoAssina(p.codigo)).length
    : 0;

  const avisos: string[] = [];
  const assina = (c: string) => assinaVisto(alvo, r, c,
    unidade.get(dados.permissoes.find((p) => p.codigo === c)?.acesso ?? ''), ver);
  if (ver('ensaios') === 2 && assina('ensaio.aprovar')) {
    avisos.push('Lança e aprova ensaios: pode, mas o sistema recusa a aprovação '
      + 'do ensaio que a própria pessoa lançou.');
  }
  if (assina('medicao.conferir') && assina('medicao.aprovar')) {
    avisos.push('Confere e aprova medição: a mesma medição nunca terá as duas '
      + 'assinaturas dessa pessoa.');
  }

  return (
    <div className="editor-acesso">
      {pessoa ? (
        <section className="bloco-acesso">
          <h3>Tipo de usuário</h3>
          <select aria-label="Tipo de usuário" disabled={ro}
            value={(r.perfis ?? pessoa.perfis).length === 1 ? (r.perfis ?? pessoa.perfis)[0] : ''}
            onChange={(e) => {
              const c = e.currentTarget.value;
              if (!c) return;
              mudar((x) => (pessoa.perfis.length === 1 && pessoa.perfis[0] === c
                ? { ...x, perfis: undefined, niveis: {}, assinaturas: {} }
                : { ...x, perfis: [c], niveis: {}, assinaturas: {} }));
            }}>
            {(r.perfis ?? pessoa.perfis).length !== 1 && (
              <option value="">
                {(r.perfis ?? pessoa.perfis).map((c) =>
                  dados.tipos.find((t) => t.codigo === c)?.nome ?? c).join(' + ') || '— escolha —'}
              </option>
            )}
            {dados.tipos.map((t) => <option key={t.codigo} value={t.codigo}>{t.nome}</option>)}
          </select>
          <p className="dica">
            {r.perfis
              ? 'Trocar o tipo refaz o acesso a partir dele. As exceções individuais '
                + 'que já existem continuam valendo e aparecem depois de gravar.'
              : nExcecoes > 0
                ? <>{nExcecoes} exceç{nExcecoes > 1 ? 'ões' : 'ão'} ao tipo.{' '}
                  {!ro && <button type="button" className="ligacao" onClick={voltarTudo}>
                    Voltar tudo ao tipo</button>}</>
                : 'Sem exceções: esta pessoa tem exatamente o acesso do tipo.'}
          </p>
        </section>
      ) : tipo && (
        <section className="bloco-acesso">
          <p className="dica" style={{ margin: 0 }}>
            {ro
              ? 'O administrador tem todos os acessos por construção: cada permissão '
                + 'nova já nasce dele. Por isso este tipo não se edita aqui.'
              : `${tipo.pessoas} pessoa${tipo.pessoas === 1 ? '' : 's'} usa${tipo.pessoas === 1 ? '' : 'm'} `
                + 'este tipo. As exceções individuais delas não mudam.'}
          </p>
        </section>
      )}

      <section className="bloco-acesso">
        <h3>Acesso por espaço</h3>
        {dados.espacos.map((espaco) => {
          const unidades = (porEspaco.get(espaco.codigo) ?? []).filter(temNivel);
          if (!unidades.length) return null;
          const valores = unidades.map((u) => ver(u.codigo));
          const res = resumir(valores, unidades.some((u) => excecaoNivel(u.codigo)));
          const aberta = abertas.has(espaco.codigo) || unidades.length === 1;
          const maxEspaco = unidades.some((u) => u.editar > 0) ? 2 : 1;
          return (
            <div key={espaco.codigo} className="espaco-acesso">
              <div className="espaco-linha">
                {unidades.length > 1 ? (
                  <button type="button" className="abre-espaco" aria-expanded={aberta}
                    onClick={() => setAbertas((s) => {
                      const n = new Set(s);
                      if (n.has(espaco.codigo)) n.delete(espaco.codigo); else n.add(espaco.codigo);
                      return n;
                    })}>
                    <span className="nome-espaco">{espaco.nome}</span>
                    <span className="sub">
                      {res.misto ? res.texto : `${unidades.length} unidades`}
                      {res.excecao ? ' · com exceção' : ''}
                    </span>
                  </button>
                ) : (
                  <div className="abre-espaco sem-seta">
                    <span className="nome-espaco">{espaco.nome}</span>
                    <span className="sub">{unidades[0].nome}{res.excecao ? ' · exceção' : ''}</span>
                  </div>
                )}
                <Segmentos rotulo={`Nível do espaço ${espaco.nome}`}
                  valor={res.misto ? 'misto' : res.max}
                  maximoPermitido={maxEspaco as Nivel} desligado={ro}
                  aoEscolher={(n) => escolherEspaco(espaco.codigo, n)} />
              </div>
              {aberta && unidades.length > 1 && (
                <ul className="unidades">
                  {unidades.map((u) => {
                    const v = ver(u.codigo);
                    const exc = excecaoNivel(u.codigo);
                    const base = alvo.baseNivel(u.codigo);
                    return (
                      <li key={u.codigo} className={exc ? 'exc' : undefined}>
                        <span className="nome-unidade">
                          {u.nome}
                          <span className="telas">{u.telas}</span>
                          {v === null && <span className="aviso-unidade">parcial: escolha um nível para acertar</span>}
                          {exc && base !== null && (
                            <span className="aviso-unidade">
                              exceção · no tipo: {NOME_NIVEL[base]}{' '}
                              {!ro && <button type="button" className="ligacao"
                                onClick={() => escolher(u.codigo, base)}>voltar</button>}
                            </span>
                          )}
                        </span>
                        <Segmentos pequeno rotulo={u.nome} valor={v}
                          maximoPermitido={maximo(u)} desligado={ro}
                          aoEscolher={(n) => escolher(u.codigo, n)} />
                      </li>
                    );
                  })}
                </ul>
              )}
              {espaco.codigo === 'comum' && (
                <p className="dica">
                  Não abre espaço: são os dados que mais de um espaço lê.
                </p>
              )}
              {espaco.codigo === 'labmanager' && (
                <p className="dica">
                  Quais ensaios a pessoa lança não se decide aqui: vem das
                  Competências (a pessoa no método).
                </p>
              )}
            </div>
          );
        })}
      </section>

      <section className="bloco-acesso">
        <h3>Assinaturas e responsabilidades</h3>
        {dados.espacos.map((espaco) => {
          const lista = dados.permissoes.filter((p) => p.tipo === 'assinatura'
            && unidade.get(p.acesso)?.espaco === espaco.codigo);
          if (!lista.length) return null;
          return (
            <fieldset key={espaco.codigo} className="assinaturas">
              <legend>{espaco.nome}</legend>
              {lista.map((p) => {
                const u = unidade.get(p.acesso);
                const semVer = !!u && temNivel(u) && ver(u.codigo) === 0;
                const marcado = assina(p.codigo);
                return (
                  <label key={p.codigo} className={`assinatura${semVer ? ' bloqueada' : ''}`}>
                    <input type="checkbox" checked={marcado} disabled={ro || semVer}
                      onChange={(e) => marcar(p.codigo, e.currentTarget.checked)} />
                    <span>
                      {p.descricao}
                      {excecaoAssina(p.codigo) && <span className="aviso-unidade"> exceção</span>}
                      {semVer && u && <span className="sub"> precisa ver {u.nome}</span>}
                    </span>
                  </label>
                );
              })}
            </fieldset>
          );
        })}
        {avisos.map((a) => <Aviso key={a} tipo="atencao" titulo="Segregação de funções">{a}</Aviso>)}
        <p className="dica">
          Editar nunca inclui assinar. Quem assina precisa também da
          competência no método, e ninguém aprova o próprio trabalho.
        </p>
      </section>

      {pessoa && acaoDaConta && pessoa.situacao !== 'desligado' && (
        <section className="bloco-acesso">
          <h3>Conta</h3>
          <p className="dica">
            {pessoa.acoes} registro(s) na trilha assinados por esta pessoa.
            Desligar encerra o acesso e mantém a autoria.
          </p>
          <div className="acoes-conta">
            {pessoa.situacao !== 'bloqueado' && (
              <button type="button" className="botao secundario" onClick={() => acaoDaConta('convidar')}>
                {pessoa.senha_por_definir ? 'Convidar' : 'Redefinir senha'}
              </button>
            )}
            {pessoa.situacao === 'ativo' ? (
              <button type="button" className="botao discreto" onClick={() => acaoDaConta('bloquear')}>Bloquear</button>
            ) : (
              <button type="button" className="botao discreto" onClick={() => acaoDaConta('liberar')}>Liberar</button>
            )}
            <button type="button" className="botao discreto perigo-texto" onClick={() => acaoDaConta('desligar')}>
              Desligar
            </button>
          </div>
          {/* Onda 367: como a pessoa recebe os avisos é do administrador — o atalho para lá. */}
          <div className="acoes-conta">
            <Link className="botao discreto" to={`/avisos?pessoa=${pessoa.id}`}>Como esta pessoa recebe os avisos</Link>
          </div>
        </section>
      )}
      {pessoa && pessoa.situacao !== 'desligado' && (
        <AssinaturaEscaneada usuarioId={pessoa.id} rt={pessoa.responsavel_tecnico} />)}
      {pessoa?.situacao === 'desligado' && (
        <CamposDaFicha itens={[['Desligado', pessoa.desligamento_motivo]]} />
      )}

      {!ro && (
        <div className="pe-acesso">
          {mudancas.length === 0 ? (
            <p className="dica" role="status">
              Nenhuma alteração. Mude um nível ou uma assinatura e grave com um motivo.
            </p>
          ) : (
            <>
              <p className="pendencias" role="status">
                <strong>{mudancas.length} alteraç{mudancas.length > 1 ? 'ões' : 'ão'}</strong>
                {' '}por gravar{tipo && tipo.pessoas ? ` · afeta ${tipo.pessoas} pessoa${tipo.pessoas > 1 ? 's' : ''}` : ''}
              </p>
              <ul className="lista-mudancas">
                {mudancas.slice(0, 4).map((m) => <li key={m}>{m}</li>)}
                {mudancas.length > 4 && <li>e mais {mudancas.length - 4}</li>}
              </ul>
              <Campo
                rotulo="Motivo"
                dica="Vai para a trilha com o seu nome. É o que a revisão periódica de acessos lê."
                value={r.motivo}
                onChange={(e) => {
                  const motivo = e.currentTarget.value;
                  mudar((x) => ({ ...x, motivo }));
                }}
              />
              {erro ? <ErroDaApi erro={erro} /> : null}
              <div className="rodape-dialogo">
                <button type="button" className="secundario" onClick={descartar}>Descartar</button>
                <button type="button" onClick={() => void gravar()}
                  disabled={enviando || r.motivo.trim().length < 5}>
                  {enviando ? 'Gravando…' : 'Gravar'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Bloquear, liberar, desligar, trocar perfis ─────────────────── */

const TITULO = {
  bloquear: 'Bloquear acesso',
  liberar: 'Liberar acesso',
  desligar: 'Desligar usuário',
  perfis: 'Perfis de acesso',
} as const;

function FormAcao({ tipo, usuario, perfis, aoFechar, aoPronto }: {
  tipo: 'bloquear' | 'liberar' | 'desligar' | 'perfis';
  usuario: Usuario; perfis: Perfil[];
  aoFechar: () => void; aoPronto: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [escolhidos, setEscolhidos] = useState<string[]>(usuario.perfis);
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  const gravar = async () => {
    setEnviando(true); setErro(null);
    try {
      const corpo = tipo === 'perfis'
        ? { perfis: escolhidos, motivo }
        : { motivo };
      await api(`/usuarios/${usuario.id}/${tipo}`, { metodo: 'PATCH', corpo });
      aoPronto();
    } catch (e) { setErro(e); } finally { setEnviando(false); }
  };

  const invalido = motivo.trim().length < 5
    || (tipo === 'perfis' && escolhidos.length === 0);

  return (
    <Dialogo aberto titulo={`${TITULO[tipo]} — ${usuario.nome ?? usuario.login}`} aoFechar={aoFechar}>
      {tipo === 'desligar' && (
        <Aviso tipo="atencao" titulo="O que acontece, exatamente">
          O acesso é encerrado e o cadastro <strong>não é apagado</strong>.
          Esta pessoa assina <strong>{usuario.acoes}</strong> registro(s) na
          trilha de auditoria; apagá-la transformaria cada um deles em
          “feito por —”. É o que a <Rn codigo="RN-163" /> recusa, no banco,
          e não só nesta tela.
        </Aviso>
      )}

      {tipo === 'perfis' && (
        <fieldset className="lista-perfis">
          <legend>Perfis</legend>
          {perfis.map((p) => (
            <label key={p.id} className="linha-perfil">
              <input
                type="checkbox"
                checked={escolhidos.includes(p.codigo)}
                /*
                 * `marcado` e lido AQUI, e nao dentro do atualizador.
                 *
                 * O React anula `currentTarget` assim que o manipulador
                 * retorna; a funcao passada ao `set...` roda depois, ja na
                 * renderizacao. Ler `e.currentTarget.checked` la dentro
                 * levantava TypeError, e como nao havia limite de erro na
                 * arvore, a aplicacao inteira desmontava -- a tela marrom
                 * relatada em 22/08 era o fundo do painel lateral sem
                 * nada em cima.
                 */
                onChange={(e) => {
                  const marcado = e.currentTarget.checked;
                  setEscolhidos((atual) => (marcado
                    ? [...atual, p.codigo]
                    : atual.filter((c) => c !== p.codigo)));
                }}
              />
              <span>{p.nome}</span>
            </label>
          ))}
        </fieldset>
      )}

      <Campo
        rotulo="Motivo"
        dica="Vai para a trilha de auditoria com o seu nome e a data."
        value={motivo}
        onChange={(e) => setMotivo(e.currentTarget.value)}
      />
      {erro ? <ErroDaApi erro={erro} /> : null}
      <div className="rodape-dialogo">
        <button type="button" className="secundario" onClick={aoFechar}>Cancelar</button>
        <button
          type="button"
          className={tipo === 'desligar' ? 'perigo' : undefined}
          onClick={() => void gravar()}
          disabled={enviando || invalido}
        >
          {enviando ? 'Gravando…' : TITULO[tipo]}
        </button>
      </div>
    </Dialogo>
  );
}

/* ─── Novo usuário ───────────────────────────────────────────────── */

/**
 * Criar conta.
 *
 * A conta nasce **sem senha e inativa**, e a tela diz isso em voz alta
 * em vez de esconder: o passo seguinte é o convite, e é a pessoa quem
 * escolhe a senha. Um formulário de cadastro com campo "senha inicial"
 * pareceria mais prático e destruiria o valor de toda assinatura do
 * sistema — o administrador passaria a saber a senha de todo mundo.
 *
 * O funcionário vem de uma lista, não de um campo de texto: conta é de
 * pessoa, e a pessoa já está cadastrada. Quem já tem conta não aparece
 * na lista, o que torna o segundo login para o mesmo técnico impossível
 * por construção em vez de por atenção.
 */
/**
 * Valor sentinela do `<select>` para "esta pessoa ainda não é funcionário".
 *
 * Não é um uuid e nunca chega à API: o `gravar` troca por um id de verdade
 * depois de cadastrar. Fica fora do componente para não ser recriado a
 * cada render e para o teste de tela poder citá-lo.
 */
const NOVA = '__pessoa_nova__';

/**
 * Criar usuário — e, se preciso, a pessoa junto.
 *
 * Até 22/08/2026 o diálogo só *escolhia* funcionário, e não havia tela
 * nenhuma que cadastrasse um. Quem tentasse dar acesso a alguém recém
 * contratado lia "cadastre a pessoa em Funcionários" e ia procurar um
 * lugar que não existia — o mesmo beco do cliente no recebimento.
 *
 * **Funcionário e usuário continuam separados**, e isso não é burocracia.
 * Funcionário é a pessoa: é quem executa, verifica e assina, e o nome dela
 * sai no laudo com as competências que o SGQ registrou. Usuário é o login.
 * Há funcionário sem login (quem não opera o sistema) e o inverso nunca
 * pode acontecer — conta sem pessoa por trás transforma "aprovado por"
 * num endereço de e-mail.
 *
 * O que se juntou foi o **gesto**, não os conceitos: o administrador
 * preenche uma tela só. Por baixo continuam dois registros, e é isso que
 * mantém a autoria do laudo apontando para uma pessoa.
 */
function FormNovo({ perfis, candidatos, aoFechar, aoPronto }: {
  perfis: Perfil[]; candidatos: Candidato[];
  aoFechar: () => void; aoPronto: () => void;
}) {
  const [login, setLogin] = useState('');
  const [loginTocado, setLoginTocado] = useState(false);
  const [loginLivre, setLoginLivre] = useState<boolean | null>(null);
  const [email, setEmail] = useState('');
  const [funcionarioId, setFuncionarioId] = useState('');
  const [escolhidos, setEscolhidos] = useState<string[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);

  // Pessoa que ainda não é funcionário. Ver a nota de NOVA, abaixo.
  const [nome, setNome] = useState('');
  const [cargo, setCargo] = useState('');
  const [matricula, setMatricula] = useState('');
  const cadastrandoPessoa = funcionarioId === NOVA;

  /*
   * Sugerir o login a partir do nome — pelo SERVIDOR.
   *
   * A normalização mora em `fn_login_sugerido`, no banco, e é a mesma
   * que a `0052` usou para carregar os usuários antigos. Copiá-la aqui
   * em JavaScript daria `joao.dasilva` num lugar e `joao.silva` no
   * outro no dia em que uma das duas mudasse.
   *
   * Roda no BLUR do nome (e ao escolher um funcionário), não a cada
   * tecla: é uma ida ao servidor por nome digitado, não por letra.
   */
  const sugerir = async (nomeDaPessoa: string) => {
    if (loginTocado || !nomeDaPessoa.trim()) return;
    try {
      const r = await api<{ login: string | null; livre: boolean }>(
        `/usuarios/login-sugerido?nome=${encodeURIComponent(nomeDaPessoa)}`);
      if (r.login && !loginTocado) setLogin(r.login);
      setLoginLivre(r.login ? r.livre : null);
    } catch { /* sugestão é conveniência; sem ela o campo continua livre */ }
  };

  const gravar = async () => {
    setEnviando(true); setErro(null);
    try {
      /*
       * Duas gravações, e não uma transação só na API.
       *
       * Se a segunda falhar — login repetido é o caso comum — o
       * funcionário fica cadastrado. Isso é de propósito: a pessoa
       * *existe* na Testesolo independentemente de ter login, e o
       * cadastro dela não é rascunho de conta. O administrador corrige o
       * login e tenta de novo; o funcionário já vai estar na lista.
       *
       * Desfazer seria pior: apagaria um registro do quadro de pessoal
       * por causa de um erro de digitação em outro campo.
       */
      let id = funcionarioId;
      if (cadastrandoPessoa) {
        const f = await api<{ id: string }>('/funcionarios', {
          metodo: 'POST',
          corpo: {
            nome: nome.trim(),
            cargo: cargo.trim() || undefined,
            matricula: matricula.trim() || undefined,
          },
        });
        id = f.id;
      }
      await api('/usuarios', {
        metodo: 'POST',
        corpo: {
          login: login.trim().toLowerCase(),
          /*
           * E-mail OPCIONAL desde a 0052: o laboratório tem técnico e
           * auxiliar sem caixa corporativa, e inventar um endereço para
           * satisfazer o formulário é como nasce uma segunda conta do
           * mesmo João no dia em que a caixa de verdade for criada.
           */
          email: email.trim() || undefined,
          funcionarioId: id,
          perfis: escolhidos,
        },
      });
      aoPronto();
    } catch (e) { setErro(e); } finally { setEnviando(false); }
  };

  const pessoaPronta = cadastrandoPessoa ? nome.trim().length >= 3 : !!funcionarioId;
  /*
   * O login é a identidade obrigatória; o e-mail, quando informado, tem
   * de parecer um e-mail. A regra de verdade está no `CHECK` do banco —
   * esta aqui só evita a viagem até o servidor para ouvir a mesma coisa.
   */
  const loginValido = /^[a-z0-9]([a-z0-9._-]{1,62}[a-z0-9])?$/
    .test(login.trim().toLowerCase());
  const emailValido = email.trim() === '' || email.includes('@');
  const invalido = !loginValido || !emailValido || !pessoaPronta
    || escolhidos.length === 0;

  return (
    <Dialogo aberto titulo="Novo usuário" aoFechar={aoFechar}>
      <>
          {/*
            * `<select>` nativo, e não a `Escolha` em botões: o quadro de
            * funcionários cresce, e trinta botões numa caixa de diálogo
            * viram uma parede. O nativo ainda ganha busca por digitação
            * de graça no desktop e a roleta do sistema no celular.
            */}
          <label className="campo">
            <span className="rotulo">Funcionário</span>
            <select
              value={funcionarioId}
              onChange={(e) => {
                const escolhido = e.currentTarget.value;
                setFuncionarioId(escolhido);
                const c = candidatos.find((x) => x.id === escolhido);
                if (c) void sugerir(c.nome);
              }}
            >
              <option value="">— escolha —</option>
              {candidatos.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.cargo ? `${c.nome} — ${c.cargo}` : c.nome}
                </option>
              ))}
              <option value={NOVA}>+ Cadastrar pessoa nova</option>
            </select>
            <span className="dica">
              {candidatos.length === 0
                ? 'Todos os funcionários ativos já têm conta — cadastre a '
                  + 'pessoa nova aqui mesmo.'
                : 'Só aparecem funcionários ativos que ainda não têm conta.'}
            </span>
          </label>

          {cadastrandoPessoa && (
            <div className="pessoa-nova">
              <Campo
                rotulo="Nome completo"
                dica="É este nome que sai impresso no laudo, não o login."
                value={nome}
                onChange={(e) => setNome(e.currentTarget.value)}
                onBlur={(e) => void sugerir(e.currentTarget.value)}
                autoFocus
              />
              <Campo
                rotulo="Cargo"
                dica="Opcional. Aparece ao lado do nome nas listas de escolha."
                value={cargo}
                onChange={(e) => setCargo(e.currentTarget.value)}
              />
              <Campo
                rotulo="Matrícula"
                dica="Opcional. O número que o RH usa, se houver."
                value={matricula}
                onChange={(e) => setMatricula(e.currentTarget.value)}
              />
              <Aviso tipo="info" titulo="Cadastrar aqui não dá competência técnica">
                A pessoa passa a existir no quadro e pode entrar no sistema,
                mas <strong>executar, verificar ou aprovar um ensaio</strong>{' '}
                depende de autorização por método, com treinamento válido —
                isso vem do SGQ e entra com o módulo de competências. Até lá
                ela não aparece como executante nem como verificadora.
              </Aviso>
            </div>
          )}

          {/*
            * O login é o campo de acesso; o e-mail virou contato.
            *
            * A sugestão a partir do nome (`joao.silva`) é conveniência e
            * pára de valer no instante em que o administrador digita —
            * `loginTocado`. Sem essa trava, corrigir o nome do
            * funcionário sobrescreveria o login que ele acabou de
            * escolher, e ele descobriria isso depois de gravar.
            */}
          <Campo
            rotulo="Login de acesso"
            type="text"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            dica="nome.sobrenome, minúsculo, sem arroba. É com ele que a
                  pessoa entra, e ele nunca muda de dono."
            value={login}
            onChange={(e) => {
              setLoginTocado(true);
              setLoginLivre(null);
              setLogin(e.currentTarget.value.toLowerCase());
            }}
          />
          {loginLivre === false && (
            <Aviso tipo="atencao" titulo="Esse login já existe">
              Dois nomes iguais no quadro acontecem. Acrescente a inicial do
              meio ou um número — <code>joao.silva2</code> — e confira com a
              pessoa qual é o dela.
            </Aviso>
          )}
          {login.trim() !== '' && !loginValido && (
            <Aviso tipo="atencao" titulo="Login fora do formato">
              Use letras minúsculas, números, ponto, hífen ou traço baixo —
              começando e terminando por letra ou número. Arroba não entra:
              é ele que separa login de e-mail.
            </Aviso>
          )}

          <Campo
            rotulo="E-mail (opcional)"
            type="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            inputMode="email"
            dica="Só para contato. Quem não tem caixa corporativa entra
                  pelo login do mesmo jeito."
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
          />

          <fieldset className="lista-perfis">
            <legend>Perfis</legend>
            {perfis.map((p) => (
              <label key={p.id} className="linha-perfil">
                <input
                  type="checkbox"
                  checked={escolhidos.includes(p.codigo)}
                  /* Ver a nota do outro fieldset: ler o valor antes do
                     atualizador, nunca dentro dele. */
                  onChange={(e) => {
                    const marcado = e.currentTarget.checked;
                    setEscolhidos((atual) => (marcado
                      ? [...atual, p.codigo]
                      : atual.filter((c) => c !== p.codigo)));
                  }}
                />
                <span>{p.nome}</span>
              </label>
            ))}
          </fieldset>

          <Aviso tipo="info" titulo="A conta nasce sem senha">
            Ninguém define a senha de outra pessoa neste sistema. Depois de
            criar, use <strong>Convidar</strong> para gerar um link de uso
            único: quem escolhe a senha é o titular. É isso que faz
            “aprovado por Fulano” significar alguma coisa.
          </Aviso>
        </>

      {erro ? <ErroDaApi erro={erro} /> : null}
      {/*
        * O botão diz POR QUE está desligado (UX-001).
        *
        * Na auditoria de 03/09/2026 o «Criar usuário» ficou cinza sem
        * explicação porque a condição era `candidatos.length === 0` —
        * e quando todos os funcionários já têm conta, o único caminho
        * (cadastrar pessoa nova) ficava bloqueado pela própria lista
        * vazia. `invalido` já cobre a pessoa nova; a lista vazia não é
        * motivo para não criar.
        */}
      {invalido && !enviando && (
        <p className="sub" role="status">
          {!pessoaPronta
            ? (cadastrandoPessoa
              ? 'Falta o nome completo da pessoa nova.'
              : 'Escolha o funcionário — ou «Cadastrar pessoa nova».')
            : !loginValido ? 'Falta um login válido.'
              : !emailValido ? 'O e-mail não parece um e-mail.'
                : 'Marque ao menos um perfil.'}
        </p>
      )}
      <div className="rodape-dialogo">
        <button type="button" className="secundario" onClick={aoFechar}>Cancelar</button>
        <button
          type="button"
          onClick={() => void gravar()}
          disabled={enviando || invalido}
        >
          {enviando ? 'Criando…' : 'Criar usuário'}
        </button>
      </div>
    </Dialogo>
  );
}

/* ─── Convite de primeiro acesso ─────────────────────────────────── */

/**
 * Gera o link e mostra **uma vez**.
 *
 * O segredo não fica gravado em lugar nenhum que possa ser lido de
 * volta — o banco guarda só o sha-256. Fechar este diálogo sem copiar
 * significa gerar outro convite, e a tela avisa isso antes, não depois.
 *
 * Copiar usa `navigator.clipboard` quando existe e cai para “selecione e
 * copie” quando não existe (contexto sem HTTPS, navegador antigo). O que
 * não pode acontecer é o botão dizer “copiado” sem ter copiado: o
 * administrador manda uma mensagem vazia para o testador e ninguém
 * descobre até a pessoa responder que não recebeu nada.
 */
function FormConvite({ usuario, aoFechar, aoPronto }: {
  usuario: Usuario; aoFechar: () => void; aoPronto: () => void;
}) {
  const [link, setLink] = useState<string | null>(null);
  const [expira, setExpira] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const [gerando, setGerando] = useState(false);

  const gerar = async () => {
    setGerando(true); setErro(null);
    try {
      const r = await api<{ link: string; expira_em: string }>(
        `/usuarios/${usuario.id}/convite`, { metodo: 'POST', corpo: {} });
      setLink(r.link);
      setExpira(r.expira_em);
      aoPronto();
    } catch (e) { setErro(e); } finally { setGerando(false); }
  };

  const copiar = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopiado(true);
    } catch {
      // Sem permissão de área de transferência: o campo já está à vista
      // e selecionável. Não dizemos "copiado" sem ter copiado.
      setCopiado(false);
    }
  };

  const titulo = usuario.senha_por_definir
    ? 'Convite de primeiro acesso'
    : 'Redefinição de senha';

  return (
    <Dialogo aberto titulo={`${titulo} — ${usuario.nome ?? usuario.login}`} aoFechar={aoFechar}>
      {!link ? (
        <>
          <p className="sub" style={{ marginTop: 0 }}>
            Vou gerar um link de uso único para <strong>{usuario.login}</strong>,
            válido por 72 horas. Entregue o link à pessoa; ela escolhe a
            própria senha e a conta ativa sozinha.
          </p>
          {usuario.convite_pendente && (
            <Aviso tipo="atencao" titulo="Já existe um convite ativo">
              Gerar outro <strong>invalida o anterior</strong> na hora
              <Rn codigo="RN-165" />. Se o primeiro link já foi enviado,
              avise a pessoa — o que ela tem na mão vai parar de funcionar.
            </Aviso>
          )}
          {!usuario.senha_por_definir && (
            <Aviso tipo="atencao" titulo="Esta pessoa já tem senha">
              O link permite trocá-la. A senha atual continua valendo até
              que o link seja usado — quem não usar não fica sem acesso.
            </Aviso>
          )}
          {erro ? <ErroDaApi erro={erro} /> : null}
          <div className="rodape-dialogo">
            <button type="button" className="secundario" onClick={aoFechar}>Cancelar</button>
            <button type="button" onClick={() => void gerar()} disabled={gerando}>
              {gerando ? 'Gerando…' : 'Gerar link'}
            </button>
          </div>
        </>
      ) : (
        <>
          <Aviso tipo="ok" titulo="Copie agora — este link não aparece de novo">
            O sistema guarda apenas a impressão digital dele. Se fechar sem
            copiar, é só gerar outro.
          </Aviso>

          <label className="rotulo" htmlFor="link-convite">Link</label>
          <input
            id="link-convite"
            className="link-convite"
            readOnly
            value={link}
            onFocus={(e) => e.currentTarget.select()}
          />

          <p className="sub">
            Vence em {expira ? data(expira) : '72 horas'} · uso único ·
            só serve para <strong>{usuario.login}</strong>
          </p>

          <div className="rodape-dialogo">
            <button type="button" className="secundario" onClick={aoFechar}>Fechar</button>
            <button type="button" onClick={() => void copiar()}>
              {copiado ? 'Copiado ✓' : 'Copiar link'}
            </button>
          </div>
        </>
      )}
    </Dialogo>
  );
}

/**
 * A assinatura escaneada da pessoa — nasceu na onda 361 para o RT no boletim SPT (pedido do Humberto, 30/10/2026:
 * «mantenha a assinatura do Luciano com o CREA em cada página de furo de SPT»), e na onda 370 (RN-278) passou a valer
 * para toda pessoa: sai também acima do nome do ministrante e do coordenador da qualidade na lista de presença e no
 * certificado de treinamento («A assinatura do ministrante pode ser scanneada, sendo cadastrada no usuário» —
 * Humberto, 03/11/2026). Só o administrador cadastra (esta tela). Trocar ou tirar a que existe pede motivo.
 *
 * O título e o registro (CREA) continuam só para quem é RT — são do boletim.
 */
function AssinaturaEscaneada({ usuarioId, rt }: { usuarioId: string; rt: boolean }) {
  type Func = { id: string; titulo_profissional: string | null; registro_profissional: string | null; tem_assinatura: boolean };
  const [f, setF] = useState<Func | null>(null);
  const [semFuncionario, setSemFuncionario] = useState(false);
  const [png, setPng] = useState<string | null>(null);
  const [titulo, setTitulo] = useState('');
  const [registro, setRegistro] = useState('');
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const ler = useCallback(async () => {
    try {
      const x = await api<Func>(`/funcionarios/da-conta/${usuarioId}`);
      setF(x); setSemFuncionario(false); setTitulo(x.titulo_profissional ?? ''); setRegistro(x.registro_profissional ?? '');
      setPng(x.tem_assinatura ? (await api<{ png: string | null }>(`/funcionarios/${x.id}/assinatura`)).png : null);
    } catch (e) {
      if ((e as { status?: number }).status === 404) setSemFuncionario(true); else setErro(e);
    }
  }, [usuarioId]);
  useEffect(() => { void ler(); }, [ler]);
  if (semFuncionario) {
    return (
      <section className="bloco-acesso">
        <h3>Assinatura escaneada</h3>
        <p className="dica">Esta conta não está ligada a um funcionário — sem funcionário, não há assinatura nos documentos.</p>
      </section>
    );
  }
  if (!f) return erro ? <ErroDaApi erro={erro} /> : null;
  const gravarCampo = async (corpo: Record<string, string>) => {
    try { await api(`/funcionarios/${f.id}`, { metodo: 'PATCH', corpo: { ...corpo, motivo: 'dados do RT para o boletim' } }); setErro(null); } catch (e) { setErro(e); }
  };
  const gravar = async (dado: string | null) => {
    setOcupado(true);
    try {
      await api(`/funcionarios/${f.id}/assinatura`, {
        metodo: 'PUT', corpo: { png: dado, ...(f.tem_assinatura ? { motivo } : {}) } });
      setMotivo(''); setErro(null); await ler();
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  };
  const enviar = (arquivo: File | undefined) => {
    if (!arquivo) return;
    const leitor = new FileReader();
    leitor.onload = () => { void gravar(String(leitor.result)); };
    leitor.readAsDataURL(arquivo);
  };
  const faltaMotivo = f.tem_assinatura && motivo.trim().length < 5;
  return (
    <section className="bloco-acesso">
      <h3>Assinatura escaneada</h3>
      <p className="dica">
        Sai acima do nome desta pessoa na lista de presença e no certificado de treinamento (quando ministra ou
        conclui){rt ? ', e no pé de cada folha do boletim SPT das OS em que ela é o RT' : ''}. Envie a folha
        escaneada em PNG ou JPEG: a margem em branco é cortada sozinha.
      </p>
      <ErroDaApi erro={erro} />
      {rt && (
        <div className="grade-rt">
          <label>Título profissional
            <input className="campo" value={titulo} maxLength={120} placeholder="Engenheiro Civil"
              onChange={(e) => setTitulo(e.target.value)} onBlur={() => { if (titulo !== (f.titulo_profissional ?? '')) void gravarCampo({ tituloProfissional: titulo }); }} />
          </label>
          <label>Registro (CREA)
            <input className="campo" value={registro} maxLength={60} placeholder="CREA-SC 00000-0"
              onChange={(e) => setRegistro(e.target.value)} onBlur={() => { if (registro !== (f.registro_profissional ?? '')) void gravarCampo({ registroProfissional: registro }); }} />
          </label>
        </div>
      )}
      {png && <img className="assinatura-rt" src={png} alt="Assinatura gravada" />}
      {f.tem_assinatura && (
        <label>Motivo da troca ou da retirada
          <input className="campo" value={motivo} maxLength={500} placeholder="Ex.: a assinatura anterior saiu cortada"
            onChange={(e) => setMotivo(e.target.value)} />
        </label>
      )}
      <div className="acoes">
        <label className={`botao secundario${faltaMotivo || ocupado ? ' desligado' : ''}`}
          aria-disabled={faltaMotivo || ocupado}>
          {png ? 'Trocar a imagem' : 'Enviar a imagem'}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="so-leitor"
            disabled={faltaMotivo || ocupado}
            onChange={(e) => { enviar(e.target.files?.[0]); e.target.value = ''; }} />
        </label>
        {f.tem_assinatura && (
          <button type="button" className="botao perigo" disabled={faltaMotivo || ocupado}
            onClick={() => { void gravar(null); }}>
            Tirar a assinatura
          </button>
        )}
      </div>
    </section>
  );
}
